// src/screens/DevSetup.js
// ADM-HEALTH-01 (May 15, 2026) — live system health dashboard.
// Replaces the previous 223-line static setup guide with real-time checks:
// env vars, DNS records (SPF/DKIM/DMARC), Supabase reachability, Daily.co
// reachability, runtime info. Pulls from /api/admin-system-health every
// 30s while the screen is open.

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardTitle, Insight, Btn } from '../components/UI';

const F = 'system-ui,-apple-system,\"Helvetica Neue\",Arial,sans-serif';

function StatusDot({ ok, neutral }) {
  const color = neutral ? '#9aa6ad' : (ok ? '#28a745' : '#dc3545');
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: color, marginRight: 8, flexShrink: 0,
      boxShadow: ok && !neutral ? '0 0 0 3px rgba(40,167,69,0.18)' : 'none',
    }} />
  );
}

function Row({ label, ok, value, sub, neutral }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '9px 0', borderBottom: '.5px solid #f0f2f4', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
        <StatusDot ok={ok} neutral={neutral} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '.78rem', color: '#0f1e28', fontWeight: 500 }}>{label}</div>
          {sub && <div style={{ fontSize: '.62rem', color: '#7a9aaa', marginTop: 1 }}>{sub}</div>}
        </div>
      </div>
      {value !== undefined && value !== null && (
        <div style={{
          fontSize: '.66rem', fontFamily: 'Consolas,Monaco,monospace',
          color: ok ? '#28a745' : neutral ? '#7a9aaa' : '#dc3545',
          fontWeight: 600, textAlign: 'right',
          maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{value}</div>
      )}
    </div>
  );
}

export default function DevSetup() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastCheck, setLastCheck] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin-system-health');
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'health check failed');
      setData(d);
      setLastCheck(new Date());
    } catch (e) {
      setError(e.message || 'Unable to reach /api/admin-system-health');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30000); // 30s refresh
    return () => clearInterval(id);
  }, [fetchHealth]);

  const overall   = data?.summary?.overall;
  const headerBg  = overall === 'green' ? 'linear-gradient(135deg, #1d9e75 0%, #167a5d 100%)' : overall === 'red' ? 'linear-gradient(135deg, #c43c4a 0%, #8b2b35 100%)' : 'linear-gradient(135deg, #6F8FA7 0%, #4a6b80 100%)';

  return (
    <div style={{ fontFamily: F }}>
      {/* Header banner */}
      <div style={{
        background: headerBg, color: '#fff', borderRadius: 14,
        padding: '18px 22px', marginBottom: 18,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
        boxShadow: '0 4px 18px rgba(15,30,40,0.18)',
      }}>
        <div>
          <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.08em', opacity: 0.8, fontWeight: 600 }}>
            System Health
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: 4 }}>
            {loading && !data ? 'Checking…' : overall === 'green' ? 'All systems operational' : overall === 'red' ? 'Action required' : 'Status unknown'}
          </div>
          <div style={{ fontSize: '.7rem', opacity: 0.85, marginTop: 4 }}>
            {data?.summary ? data.summary.env_missing + ' env vars missing · ' + data.summary.dns_broken.length + ' DNS records broken' : 'Awaiting first check…'}
          </div>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          style={{
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff', borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
            fontSize: '.74rem', fontWeight: 600, fontFamily: F,
          }}>
          {loading ? '…' : 'Refresh now'}
        </button>
      </div>

      {error && <Insight type='warn' style={{ marginBottom: 12 }}>{error}</Insight>}

      {/* Last refresh */}
      {lastCheck && (
        <div style={{ fontSize: '.65rem', color: '#7a9aaa', textAlign: 'right', marginBottom: 10 }}>
          Last checked: {lastCheck.toLocaleTimeString()} · auto-refresh every 30s
        </div>
      )}

      {!data ? null : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 14 }}>
          {/* Env vars */}
          <Card>
            <CardTitle>Environment variables ({data.summary.env_total})</CardTitle>
            {Object.values(data.env).map(v => (
              <Row key={v.name} label={v.name} ok={v.set} value={v.mask} />
            ))}
            {data.summary.env_missing > 0 && (
              <div style={{ fontSize: '.66rem', color: '#dc3545', marginTop: 8 }}>
                Set missing vars on Vercel: <a href='https://vercel.com/sanoire/sanoire-admin/settings/environment-variables' target='_blank' rel='noopener noreferrer' style={{ color: '#dc3545', textDecoration: 'underline' }}>open settings →</a>
              </div>
            )}
          </Card>

          {/* DNS records */}
          <Card>
            <CardTitle>DNS records (sanoire.com)</CardTitle>
            <Row label='SPF (TXT root)' ok={data.dns.spf.ok}
              value={data.dns.spf.ok ? data.dns.spf.value.slice(0, 60) + (data.dns.spf.value.length > 60 ? '…' : '') : data.dns.spf.error} />
            <Row label='DKIM (resend._domainkey)' ok={data.dns.dkim.ok}
              value={data.dns.dkim.ok ? 'CNAME resolves' : 'missing — booking email will be spam-foldered'} />
            <Row label='DMARC (_dmarc)' ok={data.dns.dmarc.ok}
              value={data.dns.dmarc.ok ? data.dns.dmarc.value.slice(0, 60) + (data.dns.dmarc.value.length > 60 ? '…' : '') : data.dns.dmarc.error} />
            <Row label='MX (root)' ok={data.dns.mx.ok} value={data.dns.mx.ok ? 'configured' : data.dns.mx.error} />
            {!data.dns.dkim.ok && (
              <Insight type='warn' style={{ marginTop: 10, fontSize: '.7rem' }}>
                DKIM is the booking-flow launch blocker. See Sanoire_DNS_Setup_for_Launch.md in your workspace folder for paste-ready records.
              </Insight>
            )}
          </Card>

          {/* External services */}
          <Card>
            <CardTitle>External services</CardTitle>
            <Row label='Supabase' ok={data.supabase.ok}
              value={data.supabase.ok ? 'project ' + (data.supabase.project || '?') : (data.supabase.error || 'unreachable')}
              sub={data.supabase.team_members_count != null ? data.supabase.team_members_count + ' team members' : null} />
            <Row label='Daily.co (video calls)' ok={data.daily.ok}
              value={data.daily.ok ? 'API reachable' : (data.daily.error || 'unreachable')} />
          </Card>

          {/* Runtime info */}
          <Card>
            <CardTitle>Runtime</CardTitle>
            <Row label='Node version' neutral ok={true} value={data.runtime.node_version} />
            <Row label='Vercel env' neutral ok={true} value={data.runtime.vercel_env} />
            <Row label='Vercel region' neutral ok={true} value={data.runtime.vercel_region} />
            <Row label='Checked' neutral ok={true} value={new Date(data.runtime.checked_at).toLocaleString()} />
          </Card>
        </div>
      )}

      {data?.summary?.overall === 'green' && (
        <div style={{ marginTop: 18, padding: '14px 16px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', fontSize: '.78rem', color: '#15803d', fontFamily: F }}>
          Everything looks healthy. Launch checklist is satisfied on the infrastructure side.
        </div>
      )}
    </div>
  );
}
